import { effect, signal } from '@preact/signals';
import { services } from '../app';
import { eyeSkyBrightness } from '../astro/skybrightness';
import type { StarCatalog } from '../data/catalog';
import { DEG, type Mat3, type Vec3, clamp, enuToAltAz, mat3Apply, refractEnu } from '../lib/math';
import type { FrameContext, SkyEngine, SkyProvider } from '../render/engine';
import { airMass, closeCompanions, seenMag } from '../render/nakedeye';
import { analyseFrame, lumaOf, skyAt, skyBrightnessFrom, texture, zeroPoint, type Analysed, type Frame, type Source } from '../sensors/skycam';
import { CameraJudge, MATCH, SCALES, lensFor, matchSky, pixelDir, surePredictions, turnsFor, type Lens, type Prediction, type Solution } from '../sensors/skymatch';
import { place, previewFrom, reliving, settings, timeLive, viewMode } from '../state';
import { measuredSqm } from './skycheck';

// Camera help: switched on by the person (Sky view, or the chip in pointing mode), and only then, in pointing mode, the
// rear camera looks at the sky with them. A few times a second it takes the latest video frame, finds the points of
// light in it (src/sensors/skycam.ts) and compares them with the bright stars and planets Ahillah predicts there
// (src/sensors/skymatch.ts). The ones it finds are ringed on the sky; when bright stars that should be there are not, it
// says cloud or haze seems to be in the way. Phone video catches only the brightest stars, so it helps rather than
// decides. Frames are read on the phone and dropped: nothing is sent or saved. The camera itself is opened and closed by
// the sky (src/ui/SkyView.tsx), which shares it with "Camera behind the sky".

// ---------- What the camera says ----------
/**
 * Where camera help is: off (or not pointing); starting (waiting for the camera's first frames); 'day' (the Sun too
 * high for stars); 'elsewhere' (another time, place or saved night on show, not the sky in front of the camera);
 * 'looking' (nothing found just now); 'seeing' (`found` predictions ringed).
 */
export type CamState = 'off' | 'starting' | 'day' | 'elsewhere' | 'looking' | 'seeing';
export interface CamHelp {
  state: CamState;
  found: number;
  /** Cloud or haze seems to hide or dim the bright stars here. */
  note: 'cloud' | 'haze' | null;
}
export const OFF: CamHelp = { state: 'off', found: 0, note: null };
export const camHelp = signal<CamHelp>(OFF);
/** While something else has the sky to itself (the darkness check, which asks the person's own eyes), camera help rests. */
export const cameraHelpPaused = signal(false);
/** The camera's video is playing (src/ui/SkyView.tsx attaches it). */
export const cameraLive = signal(false);
/** The sky on show is the sky in front of the camera: now, here, and not a saved night. */
const hereAndNow = () => timeLive.peek() && !previewFrom.peek() && !reliving.peek();
/**
 * Whether camera help can look now, so its camera is worth opening: the sky on show is here and now, and dark enough
 * for stars (the Sun 6° down). By day, or with another time or place on show, the camera stays shut.
 */
export function cameraHelpCanLook(): boolean {
  const ctx = services.peek()?.engine?.context;
  return !!ctx && ctx.sunAlt <= CAMERA.darkSun && hereAndNow();
}
let video: HTMLVideoElement | null = null;
/** The sky's video element once the camera's stream plays in it, or null once it stops. */
export function cameraVideo(v: HTMLVideoElement | null) {
  video = v;
  cameraLive.value = !!v;
}

/** For checking on a real phone (window.__ahillah.cameraHelp): what the last look found, and how long it took. */
export const cameraHelpDebug: Record<string, unknown> = {};

// ---------- Tuning (to check on real phones outdoors) ----------
export const CAMERA = {
  /** How often the camera looks (ms): a few times a second is plenty, and light on the battery. */
  every: 450,
  /** The longer side of the frame looked at (px): bigger frames are scaled down first. */
  maxSide: 800,
  /** Turning faster than this (degrees a second), the frame is smeared and lags the compass: it waits. */
  steady: 6,
  /**
   * Stars and planets as faint as this (magnitude, as they look from here) are what a phone's video might catch: at
   * first `faintest`, and from 1.5 fainter than the camera has shown it surely catches (CameraJudge.sureMag) once it has,
   * up to `faintestEver`. Fewer predictions it cannot see make a chance match less likely.
   */
  faintest: 2.5,
  faintestEver: 3.5,
  /** Stars need the Sun this far down (degrees), and the camera's cloud and haze note further still. */
  darkSun: -6,
  sureSun: -9,
  /** Near the Moon its glare hides stars from the camera (degrees). */
  moonGlare: 12,
  /** A frame is read at least this often (ms), even if the video's clock seems not to move. */
  stale: 1500,
  /** For this long after the camera opens (ms) its brightness is still settling: nothing is said about cloud. */
  settle: 2500,
  /** A sky this close to the camera's black (video values) is a covered lens or a dark site's cloud: nothing is said. */
  blackish: 3,
};

// ---------- What Ahillah expects the camera to see ----------
/** Close doubles for a catalogue (render/nakedeye.ts): to a phone's camera each pair is one point, its brighter star. */
const twinsOf = new WeakMap<StarCatalog, Uint8Array>();

/** Sky positions a prediction needs: the catalogue, a way from J2000 to here (ENU), and the planets and Moon. */
export interface SkyHere {
  stars: StarCatalog;
  toEnu: (v: Vec3) => Vec3;
  bodies: { id: string; dir: Vec3; mag: number }[];
}

/**
 * The stars and planets bright enough for a phone's video (no fainter than `faintest` as they look from here, the air at
 * their height included), at least 3° up and within `reachDeg` of where the camera points: brightest first, a close
 * double as its brighter star, none within the Moon's glare. Directions as they appear (refraction included). Also the
 * Moon, if it is up.
 */
export function predictionsInView(sky: SkyHere, camToEnu: Mat3, reachDeg: number, faintest = CAMERA.faintest): { preds: Prediction[]; moon: Vec3 | null } {
  const cat = sky.stars;
  let twins = twinsOf.get(cat);
  if (!twins) twinsOf.set(cat, (twins = closeCompanions(cat.dir, cat.mag, cat.count, CAMERA.faintestEver + 0.5)));
  const fwd: Vec3 = [-camToEnu[2], -camToEnu[5], -camToEnu[8]];
  const cosReach = Math.cos(Math.min(reachDeg, 180) * DEG);
  const low = Math.sin(3 * DEG);
  const preds: Prediction[] = [];
  const add = (key: string, enu: Vec3, mag: number) => {
    if (enu[2] < low || enu[0] * fwd[0] + enu[1] * fwd[1] + enu[2] * fwd[2] < cosReach) return;
    const m = seenMag(mag, enu[2]);
    if (m <= faintest) preds.push({ key, enu: refractEnu(enu), mag: m });
  };
  for (let i = 0; i < cat.count && cat.mag[i] <= faintest; i++) {
    if (!twins[i]) add(`star:${i}`, sky.toEnu([cat.dir[i * 3], cat.dir[i * 3 + 1], cat.dir[i * 3 + 2]]), cat.mag[i]);
  }
  let moon: Vec3 | null = null;
  for (const b of sky.bodies) {
    if (b.id === 'Moon') {
      const e = sky.toEnu(b.dir);
      if (e[2] > -0.02) moon = refractEnu(e);
    } else if (b.id !== 'Sun') add(`body:${b.id}`, sky.toEnu(b.dir), b.mag);
  }
  const clear = moon ? preds.filter((p) => !near(p.enu, moon!, CAMERA.moonGlare)) : preds;
  return { preds: clear.sort((a, b) => a.mag - b.mag), moon };
}

/** A clock for timing the steps (ms). */
const clock = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const near = (a: Vec3, b: Vec3, deg: number) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2] > Math.cos(deg * DEG);
const forward = (m: Mat3): Vec3 => [-m[2], -m[5], -m[8]];
const angleDeg = (a: Vec3, b: Vec3) => Math.acos(clamp(a[0] * b[0] + a[1] * b[1] + a[2] * b[2], -1, 1)) / DEG;

// ---------- One look ----------
/** What one steady frame says, for the judge (skymatch.ts, CameraJudge), and what it found, for checking. */
export function lookAt(
  found: Analysed,
  sky: SkyHere,
  camToEnu: Mat3,
  judge: CameraJudge,
  conditions: { sunAlt: number; skyModel: number; screen: { w: number; h: number }; settling?: boolean },
): Parameters<CameraJudge['update']>[0] & { all: Source[]; lens: Lens } {
  const { bg, sources, glare } = found;
  const lens = lensFor(found.w, found.h);
  const turns = turnsFor(found.w, found.h, conditions.screen.w, conditions.screen.h);
  const reach = Math.atan(Math.hypot(lens.w, lens.h) / 2 / (lens.f * Math.min(...SCALES))) / DEG + MATCH.wide;
  const faintest = clamp(judge.sureMag + 1.5, CAMERA.faintest, CAMERA.faintestEver);
  const { preds, moon } = predictionsInView(sky, camToEnu, reach, faintest);
  // The lock, unless the frame has come round another way since (the phone turned on its side).
  const lock = judge.lock && turns.includes(judge.lock.turn) ? judge.lock : null;
  // Points and tiles as directions, at the usual width (near enough here), each way round the frame may be: the lock's
  // way once known.
  const ways = lock ? [lock.turn] : turns;
  const dirsOf = (x: number, y: number) => ways.map((turn) => mat3Apply(camToEnu, ...pixelDir({ ...lens, turn }, x, y, 1)));
  const moonInView = !!moon && angleDeg(moon, forward(camToEnu)) < reach - MATCH.wide + CAMERA.moonGlare;
  // A point that could be in the sky (above the horizon, clear of the Moon's glare) whichever way round is kept.
  const usable = sources.filter((s) => dirsOf(s.x, s.y).some((d) => d[2] > Math.sin(-1 * DEG) && !(moon && near(d, moon, CAMERA.moonGlare))));
  const free = matchSky(preds, usable, lens, camToEnu, { turns });
  const locked = lock ? matchSky(preds, usable, lens, camToEnu, { lock }) : null;
  // The stars it should surely catch, placed by the lock or the last fit (if the frame is still the same way round).
  const placing = judge.placing;
  const placed = placing.fit && turns.includes(placing.fit.turn) ? placing : { fit: null, margin: MATCH.wide / 3 };
  const sure = conditions.sunAlt <= CAMERA.sureSun && !moonInView ? surePredictions(preds, lens, camToEnu, judge.sureMag, placed.fit, placed.margin, turns) : [];
  // How patchy the sky is, from the tiles surely above the horizon and clear of lights and the Moon, whichever way round.
  const tex = texture(bg, (tx, ty) => {
    const x = (tx + 0.5) * bg.tile, y = (ty + 0.5) * bg.tile;
    if (glare.some((g) => Math.hypot(g.x - x, g.y - y) < g.r + bg.tile)) return true;
    return dirsOf(x, y).some((d) => d[2] < Math.sin(3 * DEG) || (!!moon && near(d, moon, CAMERA.moonGlare + 5)));
  });
  // The sky's brightness from the stars the camera has matched (the solution the judge will take, by the same rule).
  const mags = new Map(preds.map((p) => [p.key, p.mag]));
  const take = [free, locked].filter((s): s is Solution => !!s && (s === locked ? s.chance < MATCH.chanceLocked : s.matches.length >= 2 && s.chance < MATCH.chance));
  const best = take.sort((a, c) => c.matches.length - a.matches.length)[0];
  let skyMag = NaN;
  if (best) {
    const zp = zeroPoint(best.matches.map((m) => ({ mag: mags.get(m.key)!, flux: usable[m.src].flux, saturated: usable[m.src].saturated })));
    const arcsec = 206265 / (lens.f * best.scale);
    skyMag = skyBrightnessFrom(zp, skyAt(bg, lens.w / 2, lens.h / 2).level, bg.black, arcsec);
  }
  // Not a frame to speak about cloud from: the camera's brightness still settling, a lamp's glare, or all but black.
  const quiet = !!conditions.settling || glare.length > 0 || skyAt(bg, lens.w / 2, lens.h / 2).level - bg.black < CAMERA.blackish;
  return { free, locked, sure, sources: usable.length, texture: tex, sky: skyMag, model: conditions.skyModel, mags, quiet, all: usable, lens };
}

// ---------- The rings on the sky ----------
/** The predictions the camera has found, by key, with when each ring appeared and (once lost) when it went. */
const rings = new Map<string, { since: number; gone: number }>();
const reducedMotion = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
/** Rings fade in over this long, and out over a little longer (ms). */
const FADE_IN = 300, FADE_OUT = 600;
/** A ring's radius round a star (px), and the least it shrinks to beside a near neighbour's. */
const RING = 11, RING_MIN = 6;

/** Where a ring goes: a star or planet's direction now (true, as the engine projects it), and how big it is drawn. */
function ringTarget(e: SkyEngine, key: string): { enu: Vec3; r: number } | null {
  const [kind, id] = key.split(':');
  if (kind === 'star') {
    const i = Number(id);
    return Number.isInteger(i) && i >= 0 && i < e.stars.count ? { enu: e.frame.toEnu(e.starDir(i)), r: 0 } : null;
  }
  const b = e.bodies.find((x) => x.id === id);
  if (!b) return null;
  return { enu: e.frame.toEnu(b.dir), r: e.bodyScreen.find((x) => x.id === id)?.r ?? 0 };
}

/**
 * The rings, drawn on the sky (a provider of the engine, so they move with it): a thin, quiet circle in the colour the
 * app uses for "seen", round each star or planet the camera has found, only in pointing mode with camera help on. Two
 * close together shrink so as not to cross.
 */
export const cameraMarks: SkyProvider = {
  update(_ctx: FrameContext) {},
  overlay(g: CanvasRenderingContext2D, ctx: FrameContext) {
    const e = services.peek()?.engine;
    if (!e || !rings.size || viewMode.peek() !== 'ar' || !settings.peek().cameraHelp || cameraHelpPaused.peek()) return;
    const now = Date.now(), still = !!reducedMotion?.matches;
    const shown: { x: number; y: number; r: number; a: number }[] = [];
    for (const [key, ring] of rings) {
      const a = still ? (ring.gone ? 0 : 1) : Math.min(1, (now - ring.since) / FADE_IN) * (ring.gone ? Math.max(0, 1 - (now - ring.gone) / FADE_OUT) : 1);
      if (ring.gone && a <= 0) {
        rings.delete(key);
        continue;
      }
      const t = ringTarget(e, key);
      const p = t && ctx.camera.project(t.enu);
      if (t && p && ctx.camera.onScreen(p, 30)) shown.push({ x: p.x, y: p.y, r: Math.max(RING, t.r + 6), a });
    }
    for (const s of shown) {
      for (const o of shown) {
        if (o === s) continue;
        const d = Math.hypot(o.x - s.x, o.y - s.y);
        // Not a planet's ring round its disc: that keeps clear of the disc whatever is near.
        if (s.r === RING && d < s.r + o.r + 2) s.r = Math.max(RING_MIN, Math.min(s.r, d / 2 - 1));
      }
    }
    g.save();
    g.lineWidth = 1.3;
    g.shadowColor = 'rgba(3,5,10,0.7)';
    g.shadowBlur = 3;
    g.strokeStyle = 'rgba(126,216,164,0.78)';
    for (const s of shown) {
      g.globalAlpha = s.a;
      g.beginPath();
      g.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      g.stroke();
    }
    g.restore();
  },
};

/** Show the rings for what the camera has found now: new ones fade in, lost ones fade out. */
function ringFound(found: string[], now: number) {
  const keep = new Set(found);
  for (const k of found) {
    const r = rings.get(k);
    if (!r || r.gone) rings.set(k, { since: r && r.gone ? now - FADE_IN : now, gone: 0 });
  }
  for (const [k, r] of rings) if (!keep.has(k) && !r.gone) r.gone = now;
}

// ---------- Reading frames ----------
/**
 * How camera help reads the camera: the frame on show now, as its sky and points of light, at once, or soon (from a
 * worker), or null if there is none yet; and how it lets go of everything when switched off.
 */
export interface Eyes {
  see(v: HTMLVideoElement): Analysed | Promise<Analysed | null> | null;
  close(): void;
}

let canvas: HTMLCanvasElement | null = null;
let g2d: CanvasRenderingContext2D | null = null;

/** The video's latest frame's pixels, scaled down to `CAMERA.maxSide` at most; null if there is none yet. */
function grabPixels(v: HTMLVideoElement): { w: number; h: number; data: Uint8ClampedArray } | null {
  const vw = v.videoWidth, vh = v.videoHeight;
  if (!vw || !vh || v.readyState < 2) return null;
  const k = Math.min(1, CAMERA.maxSide / Math.max(vw, vh));
  const w = Math.round(vw * k), h = Math.round(vh * k);
  canvas ??= document.createElement('canvas');
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
    g2d = null;
  }
  g2d ??= canvas.getContext('2d', { willReadFrequently: true });
  if (!g2d) return null;
  try {
    g2d.drawImage(v, 0, 0, w, h);
    return { w, h, data: g2d.getImageData(0, 0, w, h).data };
  } catch {
    return null;
  }
}

/** Let go of the canvas the frames are read through. */
function releaseCanvas() {
  if (canvas) canvas.width = canvas.height = 0;
  canvas = null;
  g2d = null;
}

/** Eyes that analyse each frame on this thread, as `grab` gives it (made-up frames in tests). */
export function frameEyes(grab: (v: HTMLVideoElement) => Frame | null): Eyes {
  return { see: (v) => { const f = grab(v); return f && analyseFrame(f); }, close: () => {} };
}

/** A worker gets this long to answer (ms, counted only while the page is in view); slower, and a fresh one is started. */
const WORKER_WAIT = 3000;
/** After this many workers in a row fail or fall silent, frames are worked out on this thread until switched off. */
const WORKER_TRIES = 3;

/**
 * The usual eyes: the frame read here, through a canvas (quick), and analysed in a worker (src/live/camera.worker.ts),
 * so the sky keeps moving smoothly meanwhile. A worker that fails or stops answering is replaced; where none will start
 * or keep going, frames are analysed on this thread instead, until camera help is next switched on. The pixels go to
 * the worker and nowhere else.
 */
export function workerEyes(): Eyes {
  let worker: Worker | null = null, failures = 0, seq = 0;
  const waiting = new Map<number, (a: Analysed | null) => void>();
  /** Stop the worker and let anything waiting on it go empty-handed. */
  const drop = () => {
    worker?.terminate();
    worker = null;
    waiting.forEach((done) => done(null));
    waiting.clear();
  };
  const failed = () => {
    failures++;
    drop();
  };
  const start = () => {
    if (worker || failures >= WORKER_TRIES) return worker;
    try {
      worker = new Worker(new URL('./camera.worker.ts', import.meta.url), { type: 'module' });
      worker.onmessage = (e: MessageEvent<Analysed & { id: number }>) => {
        failures = 0;
        const done = waiting.get(e.data.id);
        waiting.delete(e.data.id);
        done?.(e.data);
      };
      worker.onerror = failed;
    } catch {
      failures = WORKER_TRIES;
    }
    return worker;
  };
  const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
  return {
    see(v) {
      const img = grabPixels(v);
      if (!img) return null;
      const w = start();
      if (!w) return analyseFrame(lumaOf(img.data, img.w, img.h));
      const id = ++seq;
      return new Promise<Analysed | null>((resolve) => {
        // The wait only counts while the page is in view: in the background a worker may simply be paused.
        let left = WORKER_WAIT, late: ReturnType<typeof setTimeout>;
        const tick = () => {
          if (!hidden()) left -= 500;
          if (left <= 0) return failed();
          late = setTimeout(tick, 500);
        };
        late = setTimeout(tick, 500);
        waiting.set(id, (a) => {
          clearTimeout(late);
          resolve(a);
        });
        w.postMessage({ id, w: img.w, h: img.h, rgba: img.data.buffer }, [img.data.buffer]);
      });
    },
    close() {
      drop();
      failures = 0;
      releaseCanvas();
    },
  };
}

// ---------- Looking, a few times a second ----------
/** The sky here, for predictions, from the engine as it last drew it. */
const skyOf = (e: SkyEngine): SkyHere => ({ stars: e.stars, toEnu: (v) => e.frame.toEnu(v), bodies: e.bodies });

/** The model's clear-sky brightness (mag/arcsec²) where the camera points: the zenith's, brighter towards the horizon. */
function skyModel(e: SkyEngine, camToEnu: Mat3): number {
  const set = settings.peek(), ctx = e.context;
  const moon = e.bodies.find((b) => b.id === 'Moon');
  const moonAlt = moon ? enuToAltAz(e.frame.toEnu(moon.dir)).alt : -90;
  const zenith = eyeSkyBrightness(ctx.sunAlt, moonAlt, moon?.mag ?? -10, set.bortle, measuredSqm(place.peek()) ?? (set.bortleAuto ? set.sqm : null));
  return zenith - 2.5 * Math.log10(Math.min(3, airMass(Math.max(0.1, forward(camToEnu)[2]))));
}

/**
 * Camera help's watch, from when Ahillah opens (App): while it is on and pointing, it looks a few times a second; off,
 * or not pointing, it forgets everything and lets go of its frames and worker. `eyes` read the frames (made-up ones in
 * tests). Returns the clean-up.
 */
export function watchCameraHelp(eyes: Eyes = workerEyes()): () => void {
  const judge = new CameraJudge();
  let timer: ReturnType<typeof setTimeout> | undefined;
  /** The phone as last looked at: where it pointed, which way was up, and when. */
  let last: { fwd: Vec3; up: Vec3; at: number } | null = null;
  /** When the camera's video was first seen playing (ms; 0 while it is not): its brightness settles over a moment. */
  let liveSince = 0;
  /** The video's clock at the last frame read, and when that was (ms). */
  let lastTime = -1, lastRead = 0;
  let where = '';
  /** A frame being analysed, and which switching on it belongs to (a result from before an off is dropped). */
  let busy = false;
  let run = 0;
  const publish = (next: CamHelp) => {
    const cur = camHelp.peek();
    if (cur.state !== next.state || cur.found !== next.found || cur.note !== next.note) camHelp.value = next;
  };
  /** No frame judged this time: rings past their hold still go, and the count with them. What is left is returned. */
  const tidy = (now: number) => {
    const found = judge.tidy(now);
    ringFound(found, now);
    const cur = camHelp.peek();
    if (cur.found !== found.length && cur.state !== 'off') publish({ ...cur, found: found.length, state: found.length ? 'seeing' : cur.state === 'seeing' ? 'looking' : cur.state });
    return found.length;
  };
  /** Not looking at the sky in front of the camera just now: rings go, and what was seen no longer counts. */
  const away = (state: CamState) => {
    judge.forgetFrames();
    ringFound([], Date.now());
    publish({ state, found: 0, note: null });
  };
  /** A frame analysed: what it says about the sky, judged with the phone as it was held when the frame was read. */
  const judged = (found: Analysed, e: SkyEngine, cam: Mat3, at: number, t0: number, sunAlt: number, read: number) => {
    const screen = typeof innerWidth === 'number' ? { w: innerWidth, h: innerHeight } : { w: found.w, h: found.h };
    const t1 = clock();
    const look = lookAt(found, skyOf(e), cam, judge, { sunAlt, skyModel: skyModel(e, cam), screen, settling: at - liveSince < CAMERA.settle });
    const verdict = judge.update(look, at);
    ringFound(verdict.found, at);
    Object.assign(cameraHelpDebug, {
      ms: Math.round(clock() - t0), read: Math.round(read * 10) / 10, here: Math.round((clock() - t1) * 10) / 10, worker: (found as { ms?: number }).ms, frame: `${found.w}x${found.h}`,
      sources: look.all.length, lock: judge.lock, sureMag: judge.sureMag, sure: look.sure, free: look.free, locked: look.locked, texture: look.texture,
      sky: look.sky, model: look.model, quiet: look.quiet, verdict,
    });
    publish({ state: verdict.found.length ? 'seeing' : 'looking', found: verdict.found.length, note: verdict.note });
  };
  const tick = () => {
    // Nothing to do in the background (the camera is asked for again only once Ahillah is back in view).
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    const s = services.peek();
    const e = s?.engine, ctx = e?.context;
    if (!e || !ctx || !e.stars) return publish({ state: 'starting', found: 0, note: null });
    // Waiting for the dark, or for the sky here and now: the camera is not even opened (src/ui/SkyView.tsx asks
    // cameraHelpCanLook), and opens by itself once it can help.
    if (!hereAndNow()) return away('elsewhere');
    if (ctx.sunAlt > CAMERA.darkSun) return away('day');
    const now = Date.now();
    // No video (starting, or the stream dropped): nothing is being seen, so no rings.
    if (!video || !cameraLive.peek()) {
      liveSince = 0;
      return away('starting');
    }
    liveSince ||= now;
    if (busy || cameraHelpPaused.peek()) return;
    const p = place.peek(), here = `${p.lat.toFixed(3)},${p.lon.toFixed(3)}`;
    if (here !== where) {
      where = here;
      judge.reset();
    }
    // An iPhone still finding north holds the view still while the phone moves: the frame and the view do not agree.
    if (!e.orientation.northKnown) return void tidy(now);
    const cam = Float64Array.from(e.camera.camToEnu);
    const fwd = forward(cam), up: Vec3 = [cam[1], cam[4], cam[7]], prev = last;
    last = { fwd, up, at: now };
    // Turning (or rolling) faster than CAMERA.steady: the frame is smeared and lags the compass, so wait.
    const secs = prev ? Math.max(0.05, (now - prev.at) / 1000) : 0;
    if (!prev || Math.max(angleDeg(prev.fwd, fwd), angleDeg(prev.up, up)) / secs > CAMERA.steady) return void tidy(now);
    // A frame not seen before (the video's clock moves on with each new one); should a browser's clock not move for a
    // live camera, a look every so often all the same.
    if (video.currentTime === lastTime && now - lastRead < CAMERA.stale) return;
    lastTime = video.currentTime;
    lastRead = now;
    const t0 = clock();
    const seen = eyes.see(video);
    if (!seen) return;
    const read = clock() - t0;
    if (!(seen instanceof Promise)) return judged(seen, e, cam, now, t0, ctx.sunAlt, read);
    busy = true;
    const mine = run;
    void seen.then((found) => {
      if (mine !== run) return;
      busy = false;
      // Still on, pointing and here and now: anything else, and the frame says nothing about the sky in view.
      if (found && settings.peek().cameraHelp && viewMode.peek() === 'ar' && !cameraHelpPaused.peek() && hereAndNow()) {
        judged(found, e, cam, now, t0, ctx.sunAlt, read);
      }
    });
  };
  const schedule = () => {
    timer = setTimeout(() => {
      try {
        tick();
      } finally {
        if (timer !== undefined) schedule();
      }
    }, CAMERA.every);
  };
  /**
   * Not pointing, or switched off: stop looking and let go of the frames and the worker. Switched off, everything is
   * forgotten; only out of pointing mode for a moment (a finger on the sky), the compass's error found and what the
   * camera has shown it catches are kept, so it lines up again at once.
   */
  const rest = (off: boolean) => {
    clearTimeout(timer);
    timer = undefined;
    run++;
    busy = false;
    if (off) judge.reset();
    else judge.forgetFrames();
    rings.clear();
    last = null;
    liveSince = 0;
    lastTime = -1;
    eyes.close();
  };
  const stop = effect(() => {
    const on = settings.value.cameraHelp && viewMode.value === 'ar';
    if (on && timer === undefined) {
      publish({ state: 'starting', found: 0, note: null });
      schedule();
    } else if (!on && timer !== undefined) {
      rest(!settings.peek().cameraHelp);
      publish(OFF);
    }
  });
  return () => {
    stop();
    rest(true);
    camHelp.value = OFF;
  };
}
